import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import ListingContext from './providers/Listing/Listing.context';
import PortfolioSubnav from '../../../../components/PortfolioSubnav';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import BossModeBanner from './subrouters/ListingInformation/components/BossModeBanner';
import BackLink from 'components/BackLink';
import { useTranslation } from 'react-i18next';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { Colleague, DealRoom } from 'models/DealRoom';
import DealProvider from './providers/Deal/Deal.provider';
import { EnterpriseBanner } from './subrouters/ListingInformation/components/EnterpriseBanner';
import AuthContext from 'providers/Auth/Auth.context';
import EyeIcon from 'icons/Eye.icon';
import LockclosedIcon from 'icons/Lockclosed.icon';
import PauseIcon from 'icons/Pause.icon';
import PencilIcon from 'icons/Pencil.icon';
import { LISTING_STAGE, ListingStage } from 'models/Listing';
import { OPEN_DEAL_STATUS_HASH, SELLING_PORTFOLIO_ROUTE } from 'data/global';
import { getListingStage } from 'utils';
import ChangeDealStatusDrawer from './components/ChangeDealStatusDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import ManageDealStatusButton from './components/ManageDealStatusButton';
import TransferListingDrawer from './components/TransferListingDrawer';
import { ManageListViewDrawer } from '../../../../components/ManageListViewDrawer';
import { SHOW_TABLE_COLUMNS } from 'models/User';
import {
  REQUIRED_COLUMNS,
  TABLE_COLUMNS,
} from './components/LeadsTableContainer/data';
import BrokerPulsCheckDrawer from './components/BrokerPulsCheckDrawer';
import { sortColumnTableByReordered } from './utils';

import './ListingDetailSeller.styles.scss';

const DONT_SHOW_LISTING_STATUS: ListingStage[] = [
  LISTING_STAGE.NO_LISTING,
  LISTING_STAGE.ENDED,
];

const TITLE: Partial<Record<ListingStage, string>> = {
  [LISTING_STAGE.PUBLIC]: 'publicListing',
  [LISTING_STAGE.PRIVATE]: 'privateListing',
  [LISTING_STAGE.PAUSED]: 'pausedListing',
  [LISTING_STAGE.DRAFT]: 'draftListing',
};

const ICON: Partial<Record<ListingStage, any>> = {
  [LISTING_STAGE.PUBLIC]: EyeIcon,
  [LISTING_STAGE.PRIVATE]: LockclosedIcon,
  [LISTING_STAGE.PAUSED]: PauseIcon,
  [LISTING_STAGE.DRAFT]: PencilIcon,
};

const ListingDetailSellerRouter: React.FC = () => {
  const { previousLocation, latestActivityMetricsLocation } =
    useContext(AuthContext);
  const {
    currentUser: { _id: currentUserId, leadTableColumns },
    isSuperAdmin,
    hasEnterprise,
  } = useContext(CurrentUserContext);
  const {
    listing,
    dealRoom,
    isTableView,
    canManageDeal,
    areActionsDisabled,
    hasUnlockPremiumFeatures,
    canAccessDataRoom,
    setListing,
    setDealRoom,
  } = useContext(ListingContext);

  const { userId, listingTitle, isOwner } = listing || {};
  const {
    _id: dealRoomId,
    name,
    listingStage,
    wantToBePrivate,
  } = dealRoom || {};

  const [selectedColleague, setSelectedColleague] = useState<Colleague>();

  const [changeStatusDrawer, changeStatusDrawerRef] =
    useCallbackRef<SideDrawerRef>();
  const [transferDrawer, transferDrawerRef] = useCallbackRef<SideDrawerRef>();
  const [manageListDrawer, manageListDrawerRef] =
    useCallbackRef<SideDrawerRef>(null);
  const [brokerPulsCheck, brokerPulsCheckRef] = useCallbackRef<SideDrawerRef>();

  const hasBrokerPulsCheckOpenedOnce = useRef(false);

  const showBossModeBanner = useMemo(
    () => isSuperAdmin && currentUserId !== userId,
    [currentUserId, isSuperAdmin, userId],
  );

  const showEnterpriseBanner = useMemo(
    () => areActionsDisabled && hasEnterprise,
    [areActionsDisabled, hasEnterprise],
  );

  const showTransferDeal = useMemo(
    () => isOwner || isSuperAdmin,
    [isOwner, isSuperAdmin],
  );

  const { pathname, hash } = useLocation();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const manageTableOptions = useMemo(
    () =>
      sortColumnTableByReordered(TABLE_COLUMNS, leadTableColumns).map((el) => ({
        value: el,
        enabled: !REQUIRED_COLUMNS.includes(el),
        label: t(el),
      })),
    [leadTableColumns, t],
  );

  const initialHideValues = useMemo(
    () => TABLE_COLUMNS.filter((el) => !leadTableColumns.includes(el)),
    [leadTableColumns],
  );

  const prevTo = useMemo(() => {
    if (!previousLocation) return '../';

    const { pathname, search } = previousLocation || {};

    return pathname === SELLING_PORTFOLIO_ROUTE
      ? search
        ? `${pathname}${search}`
        : pathname
      : '..';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ListingTitle = useMemo(() => {
    const stage = getListingStage(listingStage, wantToBePrivate);

    if (DONT_SHOW_LISTING_STATUS.includes(stage)) return null;

    const Icon = ICON[stage];

    return (
      <Link to="./listing-information" className="listing-title">
        <Icon />
        <strong>{t(TITLE[stage]) + ': '}</strong>
        <p>{listingTitle || t('incomplete')}</p>
      </Link>
    );
  }, [listingStage, listingTitle, wantToBePrivate, t]);

  const openDealStatusDrawer = useCallback(() => {
    if (canManageDeal) {
      changeStatusDrawer?.setOpen(true);
    }
  }, [canManageDeal, changeStatusDrawer]);

  const openTransferDrawer = useCallback(
    (colleague?: Colleague) => {
      if (showTransferDeal) {
        transferDrawer?.setOpen(true);
        setSelectedColleague(colleague);
      }
    },
    [showTransferDeal, transferDrawer],
  );

  const openManageListView = useCallback(
    () => manageListDrawer?.setOpen(true),
    [manageListDrawer],
  );

  const updateDealRoom = useCallback(
    (dealRoom: Partial<DealRoom>) => {
      setDealRoom((prev) => ({ ...prev, ...dealRoom }));
    },
    [setDealRoom],
  );

  const updateDealRoomWithSocketEvent = useCallback(
    (newDealRoom: DealRoom) => {
      if (dealRoomId === newDealRoom._id) {
        updateDealRoom(newDealRoom);
      }
    },
    [dealRoomId, updateDealRoom],
  );

  useSocketEvent(LISTEN_EVENTS.LISTING_PAID, updateDealRoomWithSocketEvent);

  useEffect(() => {
    if (hash === OPEN_DEAL_STATUS_HASH && changeStatusDrawer) {
      openDealStatusDrawer();
      navigate(pathname, { replace: true });
    }
  }, [changeStatusDrawer, hash, pathname, openDealStatusDrawer, navigate]);

  useEffect(() => {
    if (
      dealRoom?.shouldCheckPulse &&
      canManageDeal &&
      !hasBrokerPulsCheckOpenedOnce.current &&
      brokerPulsCheck
    ) {
      brokerPulsCheck?.setOpen(true);
      hasBrokerPulsCheckOpenedOnce.current = true;
    }
  }, [brokerPulsCheck, canManageDeal, dealRoom?.shouldCheckPulse]);

  return (
    <DealProvider
      dealRoom={dealRoom}
      setDealRoom={setDealRoom}
      openDealStatusDrawer={openDealStatusDrawer}
      openTransferDrawer={openTransferDrawer}
      openManageListView={openManageListView}
    >
      <div className="listing-detail-seller-router">
        {showBossModeBanner && <BossModeBanner />}
        {showEnterpriseBanner && <EnterpriseBanner />}

        <nav className="listing-detail-seller-router__back-links">
          <BackLink to={prevTo}>{t('portfolio')}</BackLink>
          {latestActivityMetricsLocation && (
            <Link to={latestActivityMetricsLocation}>
              {t('goBackToActivityMetrics')}
            </Link>
          )}
        </nav>

        <div className="listing-detail-seller-router__heading">
          <h1>{name}</h1>
          <ManageDealStatusButton />
        </div>

        {ListingTitle}

        <PortfolioSubnav
          as="seller"
          listing={listing}
          dealRoom={dealRoom}
          canManageDeal={canManageDeal}
          isTableView={isTableView}
          canAccessDataRoom={canAccessDataRoom}
          hasUnlockPremiumFeatures={hasUnlockPremiumFeatures}
          openDealStatusDrawer={openDealStatusDrawer}
          openManageListView={openManageListView}
          updateDealRoom={updateDealRoom}
        />

        <ChangeDealStatusDrawer
          ref={changeStatusDrawerRef}
          deal={dealRoom}
          updateDeal={(deal) => {
            setDealRoom((prev) => ({ ...prev, ...deal }));
            setListing((prev) => ({ ...prev, stage: deal.listingStage }));
          }}
        />

        <ManageListViewDrawer
          updateProp={SHOW_TABLE_COLUMNS.LEAD}
          saveShownColumns
          initialHideValues={initialHideValues}
          options={manageTableOptions}
          ref={manageListDrawerRef}
        />

        {showTransferDeal && (
          <TransferListingDrawer
            ref={transferDrawerRef}
            selectedColleague={selectedColleague}
            closeAction={() => setSelectedColleague(null)}
          />
        )}

        <BrokerPulsCheckDrawer ref={brokerPulsCheckRef} />

        <Outlet />
      </div>
    </DealProvider>
  );
};

export default ListingDetailSellerRouter;
